import React, { useEffect, useRef, useState } from "react";
import { useChat } from "../../context/ChatContext";
import { useLocation, useNavigate } from "react-router-dom";
import { FaComments, FaTimes } from "react-icons/fa";

interface Toast {
    id: number;
    roomId: number;
    title: string;
    body: string;
}

/**
 * Global side-effect component:
 *  - Requests browser notification permission (once) for staff users.
 *  - Plays a short beep + shows browser notification + in-app toast when a
 *    message from a customer arrives and staff isn't actively viewing the inbox.
 *  - Mount once at the app root.
 */
const ChatNotifier: React.FC = () => {
    const { role, onIncomingMessage, onEscalation } = useChat();
    const location = useLocation();
    const navigate = useNavigate();
    const [toasts, setToasts] = useState<Toast[]>([]);
    const toastIdRef = useRef(1);
    const audioCtxRef = useRef<AudioContext | null>(null);

    // Ask for permission once when staff logs in.
    useEffect(() => {
        if (role !== "staff") return;
        if (typeof Notification === "undefined") return;
        if (Notification.permission === "default") {
            Notification.requestPermission().catch(() => {});
        }
    }, [role]);

    const playBeep = () => {
        try {
            const Ctx = window.AudioContext || (window as any).webkitAudioContext;
            if (!Ctx) return;
            if (!audioCtxRef.current) audioCtxRef.current = new Ctx();
            const ctx = audioCtxRef.current;
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.value = 880;
            osc.connect(gain); gain.connect(ctx.destination);
            gain.gain.setValueAtTime(0.0001, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.12, ctx.currentTime + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
            osc.start();
            osc.stop(ctx.currentTime + 0.4);
        } catch { /* best-effort */ }
    };

    useEffect(() => {
        if (role !== "staff") return;
        const unsubscribe = onIncomingMessage((e) => {
            if (e.senderRole !== "customer") return;

            const onInbox = location.pathname.startsWith("/support-inbox");
            // Always ping when message arrives — reinforces awareness even if staff is on inbox
            playBeep();

            // Browser notification (only if not currently focused on inbox page)
            if (!onInbox || document.visibilityState !== "visible") {
                if (typeof Notification !== "undefined" && Notification.permission === "granted") {
                    try {
                        const n = new Notification(`New message from ${e.senderName || "Customer"}`, {
                            body: e.body.slice(0, 120),
                            tag: `chat-${e.roomId}`,
                        });
                        n.onclick = () => {
                            window.focus();
                            navigate("/support-inbox");
                            n.close();
                        };
                    } catch { /* ignore */ }
                }
            }

            // In-app toast (deduped per room; auto-dismiss)
            setToasts((prev) => {
                const next = prev.filter((t) => t.roomId !== e.roomId);
                const toast: Toast = {
                    id: toastIdRef.current++,
                    roomId: e.roomId,
                    title: e.senderName || "Customer",
                    body: e.body,
                };
                return [...next, toast];
            });
        });
        return unsubscribe;
    }, [role, onIncomingMessage, location.pathname, navigate]);

    // Escalation notifications — when a customer asks for a human
    useEffect(() => {
        if (role !== "staff") return;
        const unsubscribe = onEscalation((e) => {
            playBeep();
            playBeep(); // double beep for escalation urgency

            const title = `${e.customerName || "Customer"} needs help`;
            const body = e.escalationReason
                ? `Escalated: ${e.escalationReason}`
                : "A customer is asking for a team member";

            // Browser notification
            if (typeof Notification !== "undefined" && Notification.permission === "granted") {
                try {
                    const n = new Notification(title, {
                        body: body.slice(0, 120),
                        tag: `escalation-${e.roomId}`,
                    });
                    n.onclick = () => {
                        window.focus();
                        navigate("/support-inbox");
                        n.close();
                    };
                } catch { /* ignore */ }
            }

            // In-app toast
            setToasts((prev) => {
                const next = prev.filter((t) => t.roomId !== e.roomId);
                const toast: Toast = {
                    id: toastIdRef.current++,
                    roomId: e.roomId,
                    title,
                    body,
                };
                return [...next, toast];
            });
        });
        return unsubscribe;
    }, [role, onEscalation, navigate]);

    // Auto-dismiss toasts after 6s
    useEffect(() => {
        if (toasts.length === 0) return;
        const timers = toasts.map((t) =>
            setTimeout(() => {
                setToasts((prev) => prev.filter((x) => x.id !== t.id));
            }, 6000),
        );
        return () => timers.forEach(clearTimeout);
    }, [toasts]);

    if (role !== "staff" || toasts.length === 0) return null;

    return (
        <div className="fixed top-20 right-4 z-[100] flex flex-col gap-2 max-w-[320px] w-full">
            {toasts.map((t) => (
                <div
                    key={t.id}
                    className="bg-white border border-gray-200 shadow-lg rounded-xl p-3 flex items-start gap-3 animate-[slideIn_.25s_ease-out]"
                >
                    <div className="w-9 h-9 rounded-full bg-primary text-white flex items-center justify-center shrink-0">
                        <FaComments size={14} />
                    </div>
                    <button
                        className="flex-1 text-left min-w-0"
                        onClick={() => {
                            navigate("/support-inbox");
                            setToasts((prev) => prev.filter((x) => x.id !== t.id));
                        }}
                    >
                        <div className="text-xs font-bold text-gray-900 truncate">New message · {t.title}</div>
                        <div className="text-xs text-gray-600 mt-0.5 line-clamp-2 break-words">{t.body}</div>
                        <div className="text-[10px] text-primary mt-1 font-semibold">Open inbox →</div>
                    </button>
                    <button
                        aria-label="Dismiss"
                        onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
                        className="w-6 h-6 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-400"
                    >
                        <FaTimes size={10} />
                    </button>
                </div>
            ))}
        </div>
    );
};

export default ChatNotifier;
